/*
 * AYER metal-gallery runtime for Kalles 5.4.0 — v3 (2026-10-09).
 *
 * CHANGES vs v2:
 *   P1. EXPLICIT MEDIA POLICY on AYER-managed products:
 *       - group-match     -> SHOWN
 *       - other-group     -> HIDDEN
 *       - explicitly "shared" (data-ayer-color-group="shared") -> SHOWN for every group
 *       - UNTAGGED on an AYER-managed product -> HIDDEN (not implicitly shared)
 *       - no selection yet -> SHOWN (every tagged + shared + untagged visible so we
 *         never flash an empty gallery during Kalles' pick_mode=no bootstrap)
 *       - empty / missing / invalid map entry for selected group -> placeholder tile
 *   P2. THUMBNAILS mirror the main gallery exactly (same policy, same timing).
 *   P3. LIGHTBOX integration: listen for 'lightbox:psw:open' and filter PhotoSwipe
 *       slides to the current group before (and after) open.
 *   P4. REAL Kalles slider API: calls hdt-slider.goToIndex() and hdt-slider.reInit()
 *       on the main media container's inner <hdt-slider>, instead of emitting an
 *       unlistened custom event.
 *   P5. CORRECT variant event: Kalles dispatches 'variant:updated' (verified by
 *       inspection of CWV-Staging-2026-08-29 global.min.js), NOT 'variant:change'.
 *   P6. AbortController-based event-listener lifecycle. rollback() calls
 *       controller.abort() and the controller stops responding to any event.
 *   P7. Section replacement (shopify:section:load/select) rebuilds the controller
 *       cleanly: aborts previous listeners, re-reads mount + media, re-applies.
 *   P8. Rapid-click correctness: dedupe compares to lastColorGroup AND lastApplyStamp
 *       is honored only after the first apply; applies run in the SAME tick so there
 *       is no race between a click and a reInit.
 *
 * Snippet contract (snippets/ayer-color-group-hero-map.liquid):
 *   <script type="application/json" data-ayer-color-group-hero-map
 *           [data-default-color-group="sterling_silver"]>
 *     { "shared": [{...}], "white_gold": [...], "yellow_gold": [...], "sterling_silver": [] }
 *   </script>
 *
 * Media tagging contract (snippets/media.liquid + snippets/media-thumb.liquid):
 *   AYER-managed products have `data-ayer-managed="true"` on each product media item.
 *   The group attribute is applied as:
 *     data-ayer-color-group="white_gold" | "yellow_gold" | "sterling_silver" | "shared"
 *   Non-AYER products emit NO data-ayer-managed attribute. The script early-returns
 *   on those products (no mount element present).
 */
(function () {
  'use strict';
  var LOG = '[ayer-metal-gallery v18]';
  // AYER v16: standard Ring Size (US) picker ladder.
  // Any size radio value NOT in this list is hidden from the standard picker
  // (its radio input + label are set display:none). The variant remains
  // reachable via a direct ?variant= link so legacy orders and bookmarks
  // continue to resolve. The out-of-ladder variant is NOT substituted;
  // the controller displays the true legacy configuration.
  var AYER_ALLOWED_SIZES_US = [
    '4.00','4.25','4.50','4.75','5.00','5.25','5.50','5.75',
    '6.00','6.25','6.50','6.75','7.00','7.25','7.50','7.75',
    '8.00','8.25','8.50','8.75','9.00','9.25','9.50','9.75',
    '10.00','10.25','10.50','10.75','11.00','11.25','11.50','11.75','12.00'
  ];
  var AYER_SIZE_OPTION_NAME = 'Ring Size (US)';
  function log() { try { console.log.apply(console, [LOG].concat([].slice.call(arguments))); } catch (e) {} }

  var METAL_TO_COLOR_GROUP = {
    '10K White Gold':  'white_gold',
    '14K White Gold':  'white_gold',
    '10K Yellow Gold': 'yellow_gold',
    '14K Yellow Gold': 'yellow_gold',
    '925 Sterling Silver': 'sterling_silver'
  };
  var SHARED = 'shared';
  var controllers = new Map();

  // ---- Controller --------------------------------------------------------
  function Controller(mountEl) {
    this.mount = mountEl;
    this.section = findAncestorSection(mountEl);
    if (!this.section) { log('no product section around mount; aborting'); return null; }
    this.sectionId = this.section.getAttribute('data-section-id') || this.section.id || null;
    this.form = this.section.querySelector('form[action*="/cart/add"]');
    this.media = this.section.querySelector('hdt-product-media, .hdt-product-media, .product__media, [data-product-media]');
    this.heroMap = this._readMap();
    this.defaultColorGroup = (mountEl.getAttribute('data-default-color-group') || '').toLowerCase() || null;
    this.lastColorGroup = null;
    this.lastApplyStamp = 0;
    this.placeholderEl = null;
    this.aborter = new AbortController();
    // --- v8 state coordination -----------------------------------------
    // We separate the init phase (page load / section load) from steady state. Once the
    // shopper takes any deliberate action, init-phase callbacks are void.
    this._initAborter = new AbortController();  // cancels init-phase timers on first user action
    this._userHasActed = false;                 // flipped true by the first isTrusted change event
    this._pendingReassertForMetal = false;      // set on user Metal change; cleared by one-shot reassert
    this._reassertInFlight = false;             // guards our own synthetic change events
    this._lastUserCarat = null;
    this._lastUserSize = null;
    // v17 2026-10-10: track which option names the shopper has explicitly clicked via a
    // trusted event. Used to block Add-to-Cart when Kalles silently fills a default value
    // for an option the shopper has not actually selected. Direct ?variant= links are a
    // deliberate exception: they are treated as a complete selection by filling all three.
    this._explicitlyClickedOptions = new Set();
    this._directVariantLinkResolved = false;
    this._fullVariants = null;
    this._fullVariantsFetchPending = false;
    // Section Rendering API request sequencing + selection identity. See
    // _resolveAndCommitVariantViaSectionRender for the race-prevention logic.
    this._currentSequence = 0;
    this._currentAbortController = null;
    this._pendingSelectionKey = null;
    this._lastCommittedSelectionKey = null;
    // --------------------------------------------------------------------
    if (!this.heroMap) { log('no color_group_hero_map; no-op'); return null; }
    // High-variant resolution runs on demand via Section Rendering API in
    // `_resolveAndCommitVariantViaSectionRender`, not via a one-shot /products/{handle}.js
    // fetch (which is capped at 250 variants per Shopify docs and gave the beyond-250 bug).
    this._wire();
    this._applySizePickerFilter();
    this.apply(true);
  }

  // v17 2026-10-10: production controller does NOT run alt-text tagging or hero-map
  // repopulation. Media color-group is set exclusively by the theme snippet
  // (snippets/media.liquid) emitting data-ayer-color-group from the product metafield
  // mapping. An explicitly empty color-group array in the hero map retains the intended
  // empty-state behavior (placeholder). The marker-based tagger that was in v16 has
  // been moved to v17/test_utilities/ayer-alt-text-tagger.test.js and is loaded ONLY
  // by test harnesses that explicitly need it; it never ships to production.

  // v16 2026-10-10: hide any Ring Size (US) radio value NOT in AYER_ALLOWED_SIZES_US
  // from the standard picker. The variant stays in the Shopify option set so
  // (a) its variant_id is preserved and (b) a direct ?variant= link still resolves.
  // When a direct link resolves to an out-of-ladder size, the radio is temporarily
  // revealed and labeled as legacy so the configuration displays truthfully.
  Controller.prototype._applySizePickerFilter = function () {
    var self = this;
    try {
      var allowed = {};
      for (var i = 0; i < AYER_ALLOWED_SIZES_US.length; i++) allowed[AYER_ALLOWED_SIZES_US[i]] = true;
      // Match both exact option name and a lenient fallback for themes that render "Ring Size" alone
      var selectors = [
        'input[type="radio"][name="' + AYER_SIZE_OPTION_NAME + '"]',
        'input[type="radio"][name="Ring Size"]'
      ];
      var hiddenCount = 0;
      for (var si = 0; si < selectors.length; si++) {
        var radios = this.section.querySelectorAll(selectors[si]);
        radios.forEach(function (r) {
          var v = r.getAttribute('value') || r.value;
          if (!v) return;
          var hide = !allowed[v];
          var id = r.id;
          var lbl = id ? self.section.querySelector('label[for="' + CSS.escape(id) + '"]') : null;
          if (hide) {
            r.setAttribute('data-ayer-size-filter-hidden', '1');
            r.style.display = 'none';
            if (lbl) { lbl.setAttribute('data-ayer-size-filter-hidden', '1'); lbl.style.display = 'none'; }
            hiddenCount++;
          } else {
            if (r.getAttribute('data-ayer-size-filter-hidden') === '1') {
              r.removeAttribute('data-ayer-size-filter-hidden');
              r.style.display = '';
              if (lbl) { lbl.removeAttribute('data-ayer-size-filter-hidden'); lbl.style.display = ''; }
            }
          }
        });
      }
      // If a hidden size radio is currently CHECKED (direct ?variant= link to a legacy
      // size resolved to this variant), reveal that single radio so the picker shows the
      // true legacy configuration and mark it visually so the shopper sees it is legacy.
      var checked = this.section.querySelector('input[type="radio"][name="' + AYER_SIZE_OPTION_NAME + '"]:checked, input[type="radio"][name="Ring Size"]:checked');
      if (checked && checked.getAttribute('data-ayer-size-filter-hidden') === '1') {
        checked.removeAttribute('data-ayer-size-filter-hidden');
        checked.style.display = '';
        var cid = checked.id;
        var clbl = cid ? self.section.querySelector('label[for="' + CSS.escape(cid) + '"]') : null;
        if (clbl) {
          clbl.removeAttribute('data-ayer-size-filter-hidden'); clbl.style.display = '';
          clbl.setAttribute('data-ayer-legacy-size', '1');
          clbl.setAttribute('title', 'Legacy ring size (not in standard picker; reachable via direct link only)');
          if (!clbl.querySelector('.ayer-legacy-size-badge')) {
            var badge = document.createElement('span');
            badge.className = 'ayer-legacy-size-badge';
            badge.textContent = ' (legacy)';
            badge.style.fontSize = '10px';
            badge.style.marginLeft = '4px';
            badge.style.color = '#9a6700';
            clbl.appendChild(badge);
          }
        }
      }
      log('size picker filter applied; hidden count=' + hiddenCount);
    } catch (e) { log('size picker filter error', e); }
  };

  Controller.prototype._readMap = function () {
    // v18 2026-10-11: reject empty-object maps so unrelated products that happen to
    // emit an empty mount cannot acquire AYER controller behavior. A valid AYER map
    // has at least one of the recognized color-group keys with a value. The owner-
    // authorized configured-but-empty state (e.g. "sterling_silver": []) still
    // qualifies because the key is present with an array value.
    try {
      var parsed = JSON.parse(this.mount.textContent || '{}');
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
      var VALID_GROUPS = ['shared','white_gold','yellow_gold','sterling_silver'];
      var hasAnyGroupKey = false;
      for (var i = 0; i < VALID_GROUPS.length; i++) {
        if (Object.prototype.hasOwnProperty.call(parsed, VALID_GROUPS[i])) { hasAnyGroupKey = true; break; }
      }
      if (!hasAnyGroupKey) { log('hero map has no recognized color-group key; treating as non-AYER'); return null; }
      return parsed;
    } catch (e) { log('invalid map JSON'); return null; }
  };

  Controller.prototype._wire = function () {
    var self = this;
    var signal = this.aborter.signal;
    // Kalles' real variant event. After Kalles finishes its variant resolution for this
    // section, update the gallery AND, if the shopper has just picked a Metal, verify
    // that no option radios drifted away from their deliberate choice. One-shot per metal
    // change via _pendingReassertForMetal; idempotent so a second variant:updated is harmless.
    document.addEventListener('variant:updated', function (e) {
      if (!self._eventTargetsThisSection(e)) return;
      self.apply();
      // v17 2026-10-10: re-apply the size-picker filter after Kalles's section rerender
      // replaced the picker DOM. No alt-text tagging in production; media color-group
      // is set by the theme snippet directly from the product metafield mapping.
      try { self._applySizePickerFilter(); } catch (ex) { log('post-variant-update size filter error', ex); }
      if (self._userHasActed && self._pendingReassertForMetal) {
        // rAF to let Kalles finish any post-resolve DOM mutations before we read state.
        requestAnimationFrame(function () { self._reassertUserPicksOnce(); });
      }
      // During init-phase (shopper has NOT yet acted) also re-run the memory/prompt restore
      // on each variant:updated. Kalles' variant resolver may re-pick a metal after we
      // cleared it (property-level `.checked = true` does not fire the MutationObserver on
      // attribute changes). Re-running restore here ensures the no-memory shopper ends up
      // with an empty Metal picker + prompt banner even when Kalles persistently reselects.
      if (!self._userHasActed && !self._initAborter.signal.aborted) {
        requestAnimationFrame(function () { self._restoreGoldMetalOrPrompt(); });
      }
    }, { capture: true, signal: signal });
    // Document-level change listener (handles shadow DOM via composedPath).
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t) return;
      var path = typeof e.composedPath === 'function' ? e.composedPath() : [t];
      var realTarget = path[0] || t;
      var inSection = false;
      for (var i = 0; i < path.length; i++) { if (path[i] === self.section) { inSection = true; break; } }
      if (!inSection) return;
      if (realTarget.tagName !== 'INPUT' && realTarget.tagName !== 'SELECT') return;
      var val = (realTarget.value || '').trim();
      var name = realTarget.getAttribute && realTarget.getAttribute('name');
      // isTrusted + !reassertInFlight means a genuine user interaction (not our own synthetic
      // re-tick, and not Kalles' programmatic re-set). We honor isTrusted strictly so Kalles'
      // auto-resets never masquerade as "new user preference" in memory.
      var isUser = e.isTrusted === true && !self._reassertInFlight;
      if (isUser) {
        if (!self._userHasActed) {
          self._userHasActed = true;
          // Cancel any pending init-phase callback (_applyUrlParams second apply, metal restore
          // retries, etc) so a newer deliberate choice is NOT overwritten by older URL values.
          try { self._initAborter.abort('user-interaction'); } catch (_) {}
        }
        if (name === 'Center Diamond Weight') self._lastUserCarat = val;
        else if (name === 'Ring Size (US)') self._lastUserSize = val;
        // v17 2026-10-10: record which option names the user has explicitly clicked.
        // Used by _enforceExplicitSelectionGate to block Add-to-Cart when Kalles
        // silently fills a default value for an option the shopper has not actually
        // selected.
        if (name) self._explicitlyClickedOptions.add(name);
      }
      // Metal change: refresh gallery; if user-driven, arm reassert for next variant:updated.
      if (realTarget.tagName === 'INPUT' && realTarget.type === 'radio' && METAL_TO_COLOR_GROUP[val]) {
        if (isUser) { self._rememberGoldMetal(); self._pendingReassertForMetal = true; }
        self.apply();
        if (!isUser && !self._userHasActed && !self._initAborter.signal.aborted) {
          requestAnimationFrame(function () { self._restoreGoldMetalOrPrompt(); });
        }
        // Always ask the Section Rendering API to resolve the authoritative variant id for the
        // current selection. This is the Shopify-documented path for high-variant products
        // (/products/{handle}.js caps at 250). The call is deduped by selection fingerprint so
        // repeated events in the same microtask collapse to one request.
        if (isUser) self._resolveAndCommitVariantViaSectionRender();
        return;
      }
      if (realTarget.tagName === 'SELECT' && METAL_TO_COLOR_GROUP[val]) {
        if (isUser) { self._rememberGoldMetal(); self._pendingReassertForMetal = true; }
        self.apply();
        if (isUser) self._resolveAndCommitVariantViaSectionRender();
        return;
      }
      // Carat / size change: resolve variant id via Section Rendering API so a beyond-250 combo
      // still produces the correct form id + visible SKU/price.
      if (isUser && realTarget.tagName === 'INPUT' && realTarget.type === 'radio' &&
          (name === 'Center Diamond Weight' || name === 'Ring Size (US)')) {
        self._resolveAndCommitVariantViaSectionRender();
      }
    }, { capture: true, signal: signal });
    // Section replacement: re-bind and re-apply.
    document.addEventListener('shopify:section:load', function (e) {
      if (self._eventTargetsThisSection(e)) { self._rebind(); self.apply(true); }
    }, { signal: signal });
    document.addEventListener('shopify:section:select', function (e) {
      if (self._eventTargetsThisSection(e)) { self.apply(true); }
    }, { signal: signal });
    // Lightbox close observer: when PhotoSwipe closes (via its own close button, Escape, swipe-
    // to-dismiss, etc.) it should restore body + html overflow on its own. Some Kalles builds
    // leave the overflow clamped. We watch for .pswp.pswp--open leaving the DOM AND, only
    // when no other overlay owns the body scroll lock, restore the overflow. "Other overlay"
    // is any sibling marker set by Kalles / third-party drawers (data-ayer-drawer-locked,
    // drawer-is-open class, etc). We err on the side of leaving it clamped if we are not
    // sure.
    (function installLightboxCloseObserver () {
      var observer = new MutationObserver(function () {
        if (document.querySelector('.pswp.pswp--open')) return;  // lightbox still open
        // Check for other drawer / overlay owners of the body scroll lock.
        var otherOwnersSelectors = [
          '[data-ayer-drawer-locked]',
          '.drawer-is-open',
          '.hdt-drawer--open',
          '.cart-drawer--open',
          '.mobile-nav--open',
          '.hdt-mobile-menu--open',
        ];
        for (var i = 0; i < otherOwnersSelectors.length; i++) {
          if (document.querySelector(otherOwnersSelectors[i])) return;  // leave lock alone
        }
        try {
          if (getComputedStyle(document.body).overflow === 'hidden') document.body.style.removeProperty('overflow');
          if (getComputedStyle(document.documentElement).overflow === 'hidden') document.documentElement.style.removeProperty('overflow');
        } catch (e) {}
      });
      try { observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] }); } catch (e) {}
      signal.addEventListener('abort', function () { try { observer.disconnect(); } catch (_) {} });
    })();
    // Lightbox: filter PhotoSwipe media before it opens.
    document.addEventListener('lightbox:psw:open', function (e) {
      if (self._eventTargetsThisSection(e)) self._filterLightbox();
    }, { capture: true, signal: signal });
    // Sibling switch button (v5): intercept clicks on [data-ayer-sibling-button]
    this.section.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest && e.target.closest('[data-ayer-sibling-button]');
      if (!btn) return;
      e.preventDefault();
      self._navigateSibling(btn);
    }, { signal: signal });
    // Auto-pick carat + size from URL params if present (sibling handoff on load)
    this._applyUrlParams();
  };

  // Gold-metal memory key: scoped to the sibling-family handle so that a shopper's gold-metal
  // choice is remembered when they come back from the silver sibling. The family is identified by
  // the gold product's own handle AND its sibling handle, so unrelated shapes cannot overwrite.
  Controller.prototype._familyKey = function () {
    var wrapper = this.section.querySelector('[data-ayer-sibling-switch]');
    var siblingHandle = wrapper && wrapper.getAttribute('data-ayer-sibling-handle');
    if (!siblingHandle) return null;
    var selfHandle = (location.pathname.match(/\/products\/([^/?]+)/) || [])[1] || '';
    if (!selfHandle) return null;
    // Sort the two handles so gold+silver and silver+gold produce the same family key.
    var sorted = [selfHandle, siblingHandle].sort();
    return 'ayer:family:' + sorted[0] + '::' + sorted[1];
  };

  Controller.prototype._rememberGoldMetal = function () {
    try {
      // Only remember when we are ON the gold side (i.e. this product has a Metal picker).
      var metalRadio = this.section.querySelector('input[type="radio"][name="Metal"]:checked');
      if (!metalRadio) return;
      var val = metalRadio.value;
      if (!METAL_TO_COLOR_GROUP[val]) return;
      if (METAL_TO_COLOR_GROUP[val] === 'sterling_silver') return;
      var key = this._familyKey();
      if (!key) return;
      sessionStorage.setItem(key + ':gold_metal', val);
      log('remembered gold metal for family:', val);
    } catch (e) { log('_rememberGoldMetal error', e); }
  };

  // ---- User-pick preservation for carat + size ---------------------------
  // Kalles' variant resolver has been observed to reset the Ring Size radio to the
  // first-available value after a Metal change, even when the exact (metal, carat, size)
  // combination maps to a real variant. We preserve the shopper's explicit picks by
  // reading the inline product JSON, confirming the combination exists, and re-ticking
  // the radio one-shot. We guard against feedback loops with _reassertInFlight.

  // ---- High-variant resolution via Shopify Section Rendering API ----------
  // Reference: https://shopify.dev/docs/storefronts/themes/product-merchandising/variants/support-high-variant-products
  // Shopify's /products/{handle}.js is capped at 250 variants, so inline product JSON cannot
  // resolve combinations beyond that (confirmed for the AYER matrix fixture with 1,980 variants).
  // The Section Rendering API returns the server-side re-rendered product section HTML for a
  // specific option-value selection; the HTML contains the correct resolved variant id, SKU,
  // price, and availability. Kalles' native Liquid already emits data-value-id="{{ value.id }}"
  // on every variant radio, so we read those IDs from the DOM — no theme / Liquid changes are
  // required to deploy this fix to production.

  // Read the server-authoritative value IDs from currently-checked radios (and dropdown buttons
  // in the color-dropdown picker type Kalles supports).
  Controller.prototype._collectCheckedValueIds = function () {
    var ids = [];
    var radios = this.section.querySelectorAll('input[type="radio"][data-value-id]:checked, [role="option"][aria-selected="true"][data-value-id]');
    for (var i = 0; i < radios.length; i++) {
      var id = radios[i].getAttribute('data-value-id');
      if (id) ids.push(id);
    }
    return ids;
  };

  // Hit the Section Rendering API with the current option-value selection and update the form's
  // hidden variant id + visible purchase controls to match the server's resolution.
  //
  // The resolver addresses the v9 defects reproduced by Codex:
  //   (a) Stale-response race (older selection's response overwriting the newer one): each
  //       request is tagged with a monotonically-increasing sequence id, and a response is
  //       only applied if its sequence equals the current one. Older responses are discarded.
  //       The underlying fetch is also aborted via AbortController when a newer selection
  //       starts, so most stale responses never arrive at all.
  //   (b) Unresolved-response leaving a stale variant id on the form: when the server returns
  //       no `input[name="id"]` value, we treat the current selection as UNRESOLVED and
  //       disable the submit controls instead of leaving the previous variant id in place.
  //   (c) `ayer:variant:resolved` dispatched with no consumer: we now also fire Kalles' native
  //       `variant:updated` event (what the theme's own picker fires) and update the form's
  //       hidden input + submit button state + accelerated-checkout button scoped to the
  //       correct product form (never falling back to another form on the page).
  //
  // Honors Shopify.routes.root for Markets locale prefixes (per Shopify docs).
  Controller.prototype._resolveAndCommitVariantViaSectionRender = function () {
    var self = this;
    try {
      var handleMatch = location.pathname.match(/\/products\/([^/?]+)/);
      if (!handleMatch) return;
      var handle = handleMatch[1];
      var sectionId = this.sectionId;
      if (!sectionId) return;
      var valueIds = this._collectCheckedValueIds();

      // Scope: find THE one product form that lives inside this AYER section. We never fall
      // back to another form on the page (previously a document.querySelector fallback could
      // mutate an unrelated form such as a quick-view or cart-drawer form).
      var liveForm = self.section.querySelector('form[action*="/cart/add"]');
      if (!liveForm) { log('[section-render] no cart-add form inside this section; abort'); return; }

      // Determine the expected number of options for a complete selection. The product has
      // N options; shopper must have picked all N before we treat the selection as
      // purchasable. Mid-selection calls still fire so Kalles' native gallery / color swatch
      // can swap imagery, but the submit controls stay disabled until completion.
      var optionCount = self.section.querySelectorAll('fieldset.hdt-product-form__input').length;
      if (optionCount === 0) {
        // Fallback: count unique option-name radios present
        var namedRadios = {};
        self.section.querySelectorAll('input[type="radio"][name]').forEach(function (r) {
          var n = r.getAttribute('name'); if (n) namedRadios[n] = true;
        });
        optionCount = Object.keys(namedRadios).length;
      }

      var isCompleteSelection = valueIds.length >= optionCount && optionCount > 0;

      // Dedupe: if the current selection matches the last-committed one AND we are not in
      // flight, nothing to do.
      var fetchKey = sectionId + '|' + valueIds.join(',');
      if (self._lastCommittedSelectionKey === fetchKey && !self._pendingSelectionKey) return;
      if (self._pendingSelectionKey === fetchKey) return;  // identical fetch already pending

      // Cancel any superseded in-flight request. Its response, even if it arrives, will be
      // discarded because self._currentSequence has advanced.
      if (self._currentAbortController) {
        try { self._currentAbortController.abort('superseded'); } catch (_) {}
      }
      var seq = (self._currentSequence || 0) + 1;
      self._currentSequence = seq;
      self._pendingSelectionKey = fetchKey;

      // While a selection is incomplete OR waiting for its authoritative response, the live
      // form must NOT allow submission of a possibly-stale variant id. Image-color switching
      // in Kalles' native lifecycle is a different code path (Kalles handles it via the
      // featured_media on the selected_variant); we never block that here.
      self._setPurchaseControlsDisabled(liveForm, true, isCompleteSelection ? 'resolving...' : 'select all options');

      if (!isCompleteSelection) {
        // No fetch when the selection is partial. We still cleared the stale variant above
        // by disabling submit; the form id stays whatever it was (so a stray submit via
        // keyboard still fails loudly rather than silently posting the wrong variant).
        self._pendingSelectionKey = null;
        return;
      }

      var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      if (routeRoot.slice(-1) !== '/') routeRoot += '/';
      var url = routeRoot + 'products/' + handle +
                '?section_id=' + encodeURIComponent(sectionId) +
                '&option_values=' + valueIds.join(',');

      var ac = (typeof AbortController === 'function') ? new AbortController() : null;
      self._currentAbortController = ac;

      // Also abort on rollback / section replacement so pending responses cannot mutate the
      // page after the controller has torn itself down.
      var abortOnRollback = function () { try { if (ac) ac.abort('rollback'); } catch (_) {} };
      try { self.aborter.signal.addEventListener('abort', abortOnRollback); } catch (_) {}

      fetch(url, { headers: { 'Accept': 'text/html' }, credentials: 'same-origin', signal: ac ? ac.signal : undefined })
        .then(function (r) { return r.ok ? r.text() : null; })
        .then(function (html) {
          // Sequence identity check: discard responses that are no longer current.
          if (seq !== self._currentSequence) {
            log('[section-render] discarding stale response, seq=' + seq + ' current=' + self._currentSequence);
            return;
          }
          if (self.aborter.signal.aborted) { log('[section-render] controller aborted; discarding response'); return; }

          if (!html) {
            // Request failed. Keep the submit controls disabled with an error label so the
            // shopper cannot post a possibly-stale variant.
            log('[section-render] no response HTML; marking unresolved');
            self._setPurchaseControlsDisabled(liveForm, true, 'temporary error - please try again');
            self._pendingSelectionKey = null;
            return;
          }

          var doc;
          try { doc = new DOMParser().parseFromString(html, 'text/html'); } catch (e) { log('[section-render] parse failed', e); self._setPurchaseControlsDisabled(liveForm, true, 'temporary error'); self._pendingSelectionKey = null; return; }

          // Read the authoritative variant id from the re-rendered form.
          var renderedForm = doc.querySelector('form[action*="/cart/add"]');
          var renderedHidden = renderedForm ? renderedForm.querySelector('input[type="hidden"][name="id"]') : null;
          var resolvedVariantId = (renderedHidden && renderedHidden.value) ? String(renderedHidden.value) : null;

          // Read SKU, price, available from the resolved VARIANT, not from the first match
          // anywhere in the HTML. Shopify exposes the selected variant inside its inline
          // product-variant JSON per the section markup. Prefer that; fall back to the
          // scoped form region only when the resolved-variant JSON is not emitted.
          var variantData = self._parseResolvedVariantFromRenderedDoc(doc, resolvedVariantId);
          var resolvedSku = variantData ? variantData.sku : null;
          var resolvedPrice = variantData ? variantData.price : null;
          var resolvedAvailable = variantData ? variantData.available : null;

          // UNRESOLVED guard: no variant id means the server could not pin this selection to
          // a real variant (combination genuinely has no corresponding variant, or a server
          // issue). Do NOT leave a prior id on the form. Clear it and disable purchase.
          if (!resolvedVariantId) {
            log('[section-render] UNRESOLVED selection; clearing form id + disabling submit');
            var liveHidden0 = liveForm.querySelector('input[type="hidden"][name="id"]');
            if (liveHidden0) liveHidden0.value = '';
            self._setPurchaseControlsDisabled(liveForm, true, 'unavailable combination');
            self._lastCommittedSelectionKey = null;
            self._pendingSelectionKey = null;
            return;
          }

          // UNAVAILABLE guard: variant exists but is out of stock / not for sale.
          if (resolvedAvailable === false) {
            log('[section-render] variant resolved but unavailable; id=' + resolvedVariantId);
            var liveHidden1 = liveForm.querySelector('input[type="hidden"][name="id"]');
            if (liveHidden1) liveHidden1.value = resolvedVariantId;
            self._setPurchaseControlsDisabled(liveForm, true, 'sold out');
            self._lastCommittedSelectionKey = fetchKey;
            self._pendingSelectionKey = null;
            return;
          }

          // Commit the authoritative variant id into the scoped form.
          var liveHidden = liveForm.querySelector('input[type="hidden"][name="id"]');
          if (liveHidden && String(liveHidden.value) !== String(resolvedVariantId)) {
            liveHidden.value = String(resolvedVariantId);
            log('[section-render] committed variant id=' + resolvedVariantId + ' sku=' + resolvedSku + ' price=' + resolvedPrice);
          }
          // v18 2026-10-11: the direct-variant-link validation is confirmed ONLY now,
          // after the section-render response has resolved the variant id to a specific
          // option tuple on THIS product. Only then does the explicit-selection gate
          // treat the deep link as a satisfied selection.
          if (self._pendingDirectVariantValidation) {
            self._directVariantLinkResolved = true;
            self._pendingDirectVariantValidation = false;
            log('[section-render] direct ?variant= link validated against this product');
          }
          // Re-enable purchase controls.
          self._setPurchaseControlsDisabled(liveForm, false);

          // Dispatch BOTH the AYER-scoped resolved event (for anything that cares) AND the
          // Kalles-native variant:updated event (what Kalles' downstream UI listens for).
          var variantObj = {
            id: parseInt(resolvedVariantId, 10),
            sku: resolvedSku,
            price: resolvedPrice,
            available: resolvedAvailable,
          };
          try { self.section.dispatchEvent(new CustomEvent('ayer:variant:resolved', { bubbles: true, detail: variantObj })); } catch (_) {}
          try {
            var kallesEvt = new CustomEvent('variant:updated', { bubbles: true, detail: { variant: variantObj, sectionId: sectionId } });
            self.section.dispatchEvent(kallesEvt);
          } catch (_) {}

          // v15 2026-10-10: swap SKU + price display using the AUTHORITATIVE server-rendered markup
          // from the section response. Replaces the v14 hard-coded "$X USD" fallback that ignored
          // currency, locale, compare-at, and Kalles-native formatting. The section-render response
          // (`doc`) was already parsed above to pull resolvedVariantId/SKU/price. The response also
          // contains the Kalles-rendered <hdt-variant-live is="sku"> and <hdt-product__price> fragments
          // with the correct currency, formatting, compare-at, sale badge, and sr-only accessibility
          // labels for the current market. Swap just those nodes in-place so Kalles renders stay
          // identical to a server-rendered load. Scoped strictly to self.section.
          try {
            var swapSelectors = [
              'hdt-variant-live[is="sku"]',
              '.hdt-product__sku',
              '.hdt-product__price',
            ];
            for (var si = 0; si < swapSelectors.length; si++) {
              var sel = swapSelectors[si];
              var curNode = self.section.querySelector(sel);
              var newNode = doc && doc.querySelector ? doc.querySelector(sel) : null;
              if (curNode && newNode) {
                // Replace innerHTML (keeps outer element reference intact so any attached listeners
                // on the outer element itself survive). Do NOT replace the outer element (that would
                // disconnect Kalles' custom-element lifecycle).
                curNode.innerHTML = newNode.innerHTML;
              }
            }
            // Re-enable purchase controls if the server returned an available variant.
            if (resolvedVariantId && resolvedAvailable !== false) {
              self._setPurchaseControlsDisabled(liveForm, false);
            }
          } catch (e) { log('display-swap error', e); }

          // v15 2026-10-10: persist the completed selection in the URL via history.replaceState
          // so reload + browser back/forward restore the same variant. Writes ayer_carat +
          // ayer_size (both products) + ayer_metal (gold only). Does not disturb other query
          // params (preview_key etc). Only replaces state when the committed variant matches
          // the current user selection, so partial/intermediate commits do not pollute history.
          try {
            if (resolvedVariantId && typeof window.history === 'object' && typeof window.history.replaceState === 'function') {
              var currentUrl = new URL(location.href);
              var qp = currentUrl.searchParams;
              var metalChecked = self.section.querySelector('input[type="radio"][name="Metal"]:checked');
              var caratChecked = self.section.querySelector('input[type="radio"][name="Center Diamond Weight"]:checked');
              var sizeChecked = self.section.querySelector('input[type="radio"][name="Ring Size (US)"]:checked');
              if (caratChecked && caratChecked.value) qp.set('ayer_carat', caratChecked.value);
              if (sizeChecked && sizeChecked.value) qp.set('ayer_size', sizeChecked.value);
              if (metalChecked && metalChecked.value) qp.set('ayer_metal', metalChecked.value);
              // Also set ?variant= for explicit variant deep-link semantics (owner directive:
              // explicit variant links override remembered state; so writing ?variant= makes the
              // URL round-trippable to the exact committed variant).
              qp.set('variant', resolvedVariantId);
              var newUrl = currentUrl.pathname + '?' + qp.toString() + (currentUrl.hash || '');
              window.history.replaceState(window.history.state, '', newUrl);
            }
          } catch (e) { log('url-persist error', e); }

          self._lastCommittedSelectionKey = fetchKey;
          self._pendingSelectionKey = null;
        })
        .catch(function (e) {
          if (seq !== self._currentSequence) return;  // stale fetch; ignore
          if (e && (e.name === 'AbortError' || String(e).indexOf('aborted') !== -1 || String(e).indexOf('superseded') !== -1)) {
            log('[section-render] fetch aborted (seq=' + seq + ')');
            return;
          }
          log('[section-render] fetch failed', e);
          self._setPurchaseControlsDisabled(liveForm, true, 'temporary error - please try again');
          self._pendingSelectionKey = null;
        });
    } catch (e) { log('_resolveAndCommitVariantViaSectionRender error', e); }
  };

  // Try to find the one resolved variant's object inside the rendered section HTML. Shopify
  // sections typically carry an inline product-variant JSON. We prefer that over free-text
  // regex matching of the full HTML (which is what v9 did, and which can match the wrong
  // variant's price when multiple variant objects are serialized).
  Controller.prototype._parseResolvedVariantFromRenderedDoc = function (doc, resolvedVariantId) {
    try {
      // 1) Shopify's inline product.js embedded script, if present on this section.
      var scripts = doc.querySelectorAll('script[type="application/json"]');
      for (var i = 0; i < scripts.length; i++) {
        var txt = scripts[i].textContent || '';
        if (!txt || txt.length < 20) continue;
        try {
          var data = JSON.parse(txt);
          // Walk a few known shapes.
          if (data && Array.isArray(data.variants)) {
            var match = data.variants.find(function (v) { return String(v.id) === String(resolvedVariantId); });
            if (match) return { sku: match.sku || null, price: typeof match.price === 'number' ? match.price : parseInt(match.price, 10) || null, available: typeof match.available === 'boolean' ? match.available : (match.inventory_quantity > 0 || match.inventory_management == null) };
          }
          if (data && data.id && String(data.id) === String(resolvedVariantId)) {
            return { sku: data.sku || null, price: typeof data.price === 'number' ? data.price : null, available: typeof data.available === 'boolean' ? data.available : null };
          }
        } catch (_) {}
      }
      // 2) Fallback: scoped to the rendered form region.
      var renderedForm = doc.querySelector('form[action*="/cart/add"]');
      var skuEl = renderedForm ? renderedForm.querySelector('[data-product-sku], .product__sku, [data-sku]') : null;
      // Price: prefer a per-form data attribute; else null.
      var priceEl = renderedForm ? renderedForm.querySelector('[data-price-amount], [data-price]') : null;
      var priceAttr = priceEl ? (priceEl.getAttribute('data-price-amount') || priceEl.getAttribute('data-price')) : null;
      // Available: prefer explicit indicator; else inferred from submit button not disabled.
      var submitEl = renderedForm ? renderedForm.querySelector('button[type="submit"], [type="submit"]') : null;
      var available = submitEl ? !submitEl.hasAttribute('disabled') : null;
      return {
        sku: skuEl ? (skuEl.textContent || '').trim().slice(0, 80) : null,
        price: priceAttr ? parseInt(priceAttr, 10) : null,
        available: available,
      };
    } catch (e) { return null; }
  };

  // Centralized enable/disable of all purchase controls scoped to the given form. Covers
  // the standard cart-add submit button AND accelerated-checkout buttons (Shop Pay, PayPal,
  // Apple Pay, Google Pay, generic dynamic-checkout) that Shopify renders separately.
  // v17 2026-10-10: enforce that every option radio fieldset in the AYER section has had
  // an explicit user click (or was resolved from a direct ?variant= deep link) before the
  // Add-to-Cart button can be enabled. This blocks Kalles's silent default-first-value
  // from presenting a complete purchasable state when the shopper has not actually
  // chosen every option. Returns { ok: true } if purchasing is allowed, else { ok: false,
  // reason: 'select <option>' } with the first missing option name.
  // v19 2026-10-11: collect option names from radios ASSOCIATED to the AYER product
  // variant-form, including radios that are NOT DOM descendants of the <form> but
  // associated via the HTML `form="<form-id>"` attribute. Kalles 5.4.0 renders option
  // radios in a separate .hdt-product-form__input picker block OUTSIDE the <form> tag;
  // v18's descendant-only selector missed them entirely, which is why a shopper could
  // commit a Kalles default size and the gate did not fire.
  //
  // Primary mechanism: form.elements (the canonical web API for form-associated
  // controls, which includes radios linked via `form=` attribute regardless of DOM
  // position). Any non-option radio (quantity, shipping, properties[...], etc.) is
  // filtered out by the name-regex guard.
  Controller.prototype._productOptionNames = function () {
    var names = new Set();
    var form = this.section.querySelector('form[action*="/cart/add"]');
    if (!form) return names;
    var elements = form.elements ? Array.from(form.elements) : [];
    for (var i = 0; i < elements.length; i++) {
      var r = elements[i];
      if (!r || r.type !== 'radio') continue;
      var nm = r.name || (r.getAttribute && r.getAttribute('name'));
      if (!nm) continue;
      if (/^(quantity|shipping_|discount_|properties\[|contact_|note|selling_plan|id$)/i.test(nm)) continue;
      names.add(nm);
    }
    return names;
  };

  Controller.prototype._enforceExplicitSelectionGate = function () {
    try {
      if (this._directVariantLinkResolved) return { ok: true };
      var optionNames = this._productOptionNames();
      if (optionNames.size === 0) return { ok: true };
      var missing = [];
      optionNames.forEach(function (nm) {
        if (!this._explicitlyClickedOptions.has(nm)) missing.push(nm);
      }.bind(this));
      if (missing.length === 0) return { ok: true };
      return { ok: false, reason: 'select ' + missing[0].toLowerCase() };
    } catch (e) { return { ok: true }; }
  };

  // Wrap the enable path: if the gate says a required option is missing, re-disable with the
  // appropriate reason instead of leaving the button enabled.
  Controller.prototype._setPurchaseControlsDisabled = function (form, disabled, reason) {
    if (!form) return;
    // v17 explicit-selection gate: never enable Add-to-Cart when any option radio
    // was not explicitly clicked (Kalles's default-first-value counts as unclicked).
    if (disabled === false) {
      var gate = this._enforceExplicitSelectionGate();
      if (!gate.ok) {
        disabled = true;
        reason = gate.reason || 'select all options';
      }
    }
    try {
      // Standard add-to-cart buttons
      var buttons = form.querySelectorAll('button[type="submit"], button[name="add"], [data-add-to-cart], input[type="submit"]');
      for (var i = 0; i < buttons.length; i++) {
        var btn = buttons[i];
        if (disabled) {
          if (!btn.hasAttribute('data-ayer-prev-disabled')) btn.setAttribute('data-ayer-prev-disabled', btn.hasAttribute('disabled') ? '1' : '0');
          btn.setAttribute('disabled', 'disabled');
          btn.setAttribute('aria-disabled', 'true');
          if (reason) btn.setAttribute('data-ayer-disable-reason', reason);
        } else {
          var prev = btn.getAttribute('data-ayer-prev-disabled');
          if (prev !== '1') btn.removeAttribute('disabled');
          btn.removeAttribute('aria-disabled');
          btn.removeAttribute('data-ayer-prev-disabled');
          btn.removeAttribute('data-ayer-disable-reason');
        }
      }
      // Accelerated-checkout wrappers (Shopify renders inside .shopify-payment-button or
      // [data-shopify="dynamic-checkout-cart"]). Toggling pointer-events + opacity + aria
      // keeps the shopper from firing them when the variant is unresolved. Scoped to this
      // form only.
      var accelerated = form.querySelectorAll('.shopify-payment-button, [data-shopify="dynamic-checkout-cart"], .additional-checkout-buttons, hdt-dynamic-checkout, .hdt-dynamic-checkout');
      for (var j = 0; j < accelerated.length; j++) {
        var el = accelerated[j];
        if (disabled) {
          el.setAttribute('data-ayer-accel-disabled', '1');
          el.style.setProperty('pointer-events', 'none', 'important');
          el.style.setProperty('opacity', '0.4', 'important');
          el.setAttribute('aria-disabled', 'true');
        } else {
          if (el.getAttribute('data-ayer-accel-disabled') === '1') {
            el.removeAttribute('data-ayer-accel-disabled');
            el.style.removeProperty('pointer-events');
            el.style.removeProperty('opacity');
            el.removeAttribute('aria-disabled');
          }
        }
      }
    } catch (e) { log('_setPurchaseControlsDisabled error', e); }
  };

  // Event-driven reassert: called from the variant:updated listener after a user-initiated
  // Metal change has flipped _pendingReassertForMetal. We read the current state AFTER
  // Kalles' resolver has finished, compare to the shopper's remembered carat + size, and
  // re-tick only the specific radios that drifted. One-shot: _pendingReassertForMetal is
  // cleared at the end so a subsequent Kalles variant:updated for the same metal change
  // does not re-trigger this.
  Controller.prototype._reassertUserPicksOnce = function () {
    try {
      if (!this._pendingReassertForMetal) return;
      this._pendingReassertForMetal = false;
      var curMetal = (this.section.querySelector('input[type="radio"][name="Metal"]:checked') || {}).value || '';
      var curCarat = (this.section.querySelector('input[type="radio"][name="Center Diamond Weight"]:checked') || {}).value || '';
      var curSize  = (this.section.querySelector('input[type="radio"][name="Ring Size (US)"]:checked') || {}).value || '';
      var targetCarat = this._lastUserCarat || curCarat;
      var targetSize  = this._lastUserSize || curSize;
      if (targetCarat === curCarat && targetSize === curSize) {
        // Nothing to re-tick; still kick a section-render in case Kalles drifted the hidden id.
        this._resolveAndCommitVariantViaSectionRender();
        return;
      }
      // We no longer gate re-ticking on an inline variant list (it is capped at 250 for high-
      // variant products and gave false "known-invalid" bails). Instead, re-tick the shopper's
      // intended carat / size, then ask the Section Rendering API to resolve the authoritative
      // variant id on the server. If the server says no such variant exists we honor that;
      // otherwise the hidden form id is updated to match the shopper's intent.
      this._reassertInFlight = true;
      try {
        if (targetCarat !== curCarat) {
          var cr = this.section.querySelector('input[type="radio"][name="Center Diamond Weight"][value="' + CSS.escape(targetCarat) + '"]');
          if (cr) { cr.checked = true; cr.dispatchEvent(new Event('change', { bubbles: true })); log('reasserted carat:', targetCarat); }
        }
        if (targetSize !== curSize) {
          var sr = this.section.querySelector('input[type="radio"][name="Ring Size (US)"][value="' + CSS.escape(targetSize) + '"]');
          if (sr) { sr.checked = true; sr.dispatchEvent(new Event('change', { bubbles: true })); log('reasserted size:', targetSize); }
        }
      } finally {
        var self = this;
        setTimeout(function () { self._reassertInFlight = false; }, 0);
      }
      // After the radios reflect the shopper's intent, request the server-rendered variant id.
      // Shopify returns the correct variant for any (metal, carat, size) combo regardless of
      // Kalles' 250-variant inline cap.
      this._resolveAndCommitVariantViaSectionRender();
    } catch (e) { log('_reassertUserPicksOnce error', e); }
  };

  // ---- Init-phase coordination loop ---------------------------------------
  // Runs on every animation frame for up to INIT_PHASE_MAX_FRAMES (~1 second at 60fps) OR
  // until the shopper takes any deliberate action. On each frame it ensures the Metal picker
  // matches either (a) the remembered gold metal, OR (b) the no-memory + arrived-from-sibling
  // clear-and-prompt state. This is NOT a progressively-longer-timer cascade: it is a bounded
  // short-duration coordinator that handles Kalles' silent variant-resolver races.
  var INIT_PHASE_MAX_FRAMES = 90;  // ~1.5 seconds at 60fps
  Controller.prototype._startInitCoordinator = function () {
    var self = this;
    var frames = 0;
    var tick = function () {
      if (self._userHasActed || self._initAborter.signal.aborted || frames >= INIT_PHASE_MAX_FRAMES) return;
      frames++;
      try { self._restoreGoldMetalOrPrompt(); } catch (_) {}
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  // ---- Init-phase URL params + memory restore ----------------------------
  // Called once from the Controller constructor. All timers / observers it installs are
  // cancellable via two signals:
  //   - this.aborter: fires on controller rollback / section replacement
  //   - this._initAborter: fires on the FIRST user action AND on controller rollback
  // The init-phase callbacks always check both signals before mutating any radio, so a newer
  // customer choice can never be overwritten by an older URL-param reapply.
  Controller.prototype._applyUrlParams = function () {
    try {
      var self = this;
      var initSignal = this._initAborter.signal;
      // Mirror aborter -> initAborter so rollback also stops init callbacks
      this.aborter.signal.addEventListener('abort', function () { try { self._initAborter.abort('controller-rollback'); } catch (_) {} });
      var params = new URLSearchParams(location.search);
      var caratParam = params.get('ayer_carat');
      var sizeParam = params.get('ayer_size');
      var metalParam = params.get('ayer_metal');  // v15: reload persistence writes ayer_metal
      var explicitVariant = params.get('variant');  // explicit Shopify variant deep-link
      // v18 2026-10-11: the mere presence of ?variant= does NOT establish that the
      // variant has been validated against this product. It could be a stale link,
      // a variant from a different product, or an unknown id. The flag is set
      // ONLY after the controller's own section-render commit confirms the id
      // belongs to this product and resolves to a specific option tuple. See
      // _resolveAndCommitVariantViaSectionRender where this flag is now set on
      // successful commit. The URL parameter itself is only used to seed the
      // option radios below.
      self._pendingDirectVariantValidation = !!explicitVariant;
      // Apply the carat / size URL params on next animation frame AND re-assert on any Kalles
      // re-render that overrides them. Verified needed in v13 2026-10-10 after v12 testing
      // caught intermittent override: Kalles' section-render response can reset the Size radio
      // back to the first available (4.00) if the carat change event fires first and the size
      // change hasn't been seen yet. MutationObservers on Carat + Size fieldsets re-assert the
      // URL params until (a) the shopper interacts, or (b) 5 seconds elapsed, whichever comes
      // first. _reassertInFlight suppresses our own change-event-driven resolution so Kalles
      // doesn't thrash.
      if ((caratParam || sizeParam || metalParam) && !explicitVariant) {
        // Coherent state transition for URL-param auto-select (v13 2026-10-10):
        // Problem seen in v12 and v13 initial fix: dispatching change events on the URL-param
        // radios causes Kalles to issue section-renders that race each other, and the earlier
        // response can reset Size to the first-available (4.00) if its carat-only query returns
        // AFTER the carat+size query. v13 solution:
        //   1. Set r.checked = true but do NOT dispatch 'change' (so Kalles' change-listener
        //      doesn't fire its own section renders).
        //   2. Let the AYER controller's own _resolveAndCommitVariantViaSectionRender() (invoked
        //      below after 650 ms) do ONE authoritative render that commits the correct variant.
        //   3. The AYER controller already dispatches `variant:updated` on commit, which is what
        //      Kalles' price/SKU/button listeners actually react to (verified by probe 3).
        //   4. A short MutationObserver on the two fieldsets catches Kalles-triggered re-renders
        //      (e.g. from Metal defaulting) and re-asserts the URL params until the shopper
        //      interacts, or 3.5 s elapses, whichever comes first.
        var setRadioOnly = function (optionName, value) {
          var r = self.section.querySelector('input[type="radio"][name="' + optionName + '"][value="' + CSS.escape(value) + '"]');
          if (!r) {
            // v16 2026-10-10: if the asked value does not exist on this sibling (e.g. ayer_size=12.5
            // arriving on silver which has no 12.5), do NOT let Kalles's default-first-value substitute
            // silently. Uncheck any currently-checked radio for this option so the shopper sees the
            // combination is unavailable instead of a silent fallback.
            var currentChecked = self.section.querySelectorAll('input[type="radio"][name="' + optionName + '"]:checked');
            if (currentChecked && currentChecked.length) {
              self._reassertInFlight = true;
              try { currentChecked.forEach(function (c) { c.checked = false; }); }
              finally { setTimeout(function () { self._reassertInFlight = false; }, 0); }
              log('url-param ' + optionName + '=' + value + ' not available on this product; uncheck to avoid silent substitution');
            }
            // v18 2026-10-11: because this value did not exist on this sibling, the option is
            // explicitly UNRESOLVED. Remove it from the explicit-click set so the gate blocks
            // Add-to-Cart until the shopper picks a supported value.
            if (self._explicitlyClickedOptions && self._explicitlyClickedOptions.delete) {
              self._explicitlyClickedOptions.delete(optionName);
            }
            return null;
          }
          if (!r.checked) { self._reassertInFlight = true; try { r.checked = true; } finally { setTimeout(function () { self._reassertInFlight = false; }, 0); } }
          // v18 2026-10-11: a URL-param-carried-over selection (sibling navigation + reload
          // persistence) counts as a satisfied option. Without this, the explicit-click gate
          // would force the shopper to re-click every option after a sibling round-trip.
          if (self._explicitlyClickedOptions && self._explicitlyClickedOptions.add) {
            self._explicitlyClickedOptions.add(optionName);
          }
          return r;
        };
        var urlParamDeadline = Date.now() + 3500;
        requestAnimationFrame(function () {
          if (initSignal.aborted) return;
          // Set BOTH radios first (no change dispatch), then fire ONE change on the LAST radio
          // so Kalles sees the final combined state and issues exactly ONE section-render for
          // the correct combination. Avoids the two-renders-racing problem that showed up when
          // we dispatched change immediately after each setRadio.
          // v15: also honor ayer_metal from reload persistence
          var metR = metalParam ? setRadioOnly('Metal', metalParam) : null;
          var carR = caratParam ? setRadioOnly('Center Diamond Weight', caratParam) : null;
          var sizR = sizeParam ? setRadioOnly('Ring Size (US)', sizeParam) : null;
          var lastSet = sizR || carR || metR;
          if (lastSet) {
            self._reassertInFlight = true;
            try { lastSet.dispatchEvent(new Event('change', { bubbles: true })); }
            finally { setTimeout(function () { self._reassertInFlight = false; }, 0); }
          }
          self._restoreGoldMetalOrPrompt();
        });
        // Watch for Kalles-driven DOM swaps that reset our URL-param radios. Observes a persistent
        // ancestor (the section itself) with subtree + childList so we catch replaced fieldsets.
        if (typeof MutationObserver === 'function') {
          var urlObs = new MutationObserver(function () {
            if (initSignal.aborted || self._userHasActed || Date.now() > urlParamDeadline) { try { urlObs.disconnect(); } catch (_) {} return; }
            // v16 2026-10-10: use setRadioOnly (closure) which both sets when value exists and
            // unchecks the Kalles-defaulted value when the asked URL value is unavailable on
            // this sibling. This prevents silent fallback to the first-ladder size (e.g. 4.00)
            // when the URL asks for a value only the other sibling has (e.g. 12.5 arriving on silver).
            if (metalParam) setRadioOnly('Metal', metalParam);
            if (caratParam) setRadioOnly('Center Diamond Weight', caratParam);
            if (sizeParam) setRadioOnly('Ring Size (US)', sizeParam);
          });
          try { urlObs.observe(self.section, { subtree: true, childList: true, attributes: true, attributeFilter: ['checked'] }); } catch (e) {}
          initSignal.addEventListener('abort', function () { try { urlObs.disconnect(); } catch (_) {} });
          setTimeout(function () { try { urlObs.disconnect(); } catch (_) {} }, 3600);
        }
      }
      // Attach a MutationObserver on the Metal fieldset so we catch Kalles' DELAYED auto-pick
      // (which can race the first requestAnimationFrame). The observer self-disconnects on
      // the first user action OR on rollback. It is a replacement for the earlier
      // 150ms+800ms+1500ms+3000ms timer cascade; it fires exactly when Kalles sets a radio.
      var metalFieldset = this.section.querySelector('fieldset.hdt-product-form__input');
      if (metalFieldset && typeof MutationObserver === 'function') {
        var mo = new MutationObserver(function () {
          if (initSignal.aborted) { mo.disconnect(); return; }
          self._restoreGoldMetalOrPrompt();
        });
        try { mo.observe(metalFieldset, { attributes: true, subtree: true, attributeFilter: ['checked'] }); } catch (e) {}
        initSignal.addEventListener('abort', function () { try { mo.disconnect(); } catch (_) {} });
      }
      // Also run restore once on next frame for the no-url-params case (e.g., direct product
      // visit via a bookmarked gold handle). This is a single call, not a cascade.
      requestAnimationFrame(function () {
        if (initSignal.aborted) return;
        self._restoreGoldMetalOrPrompt();
      });
      // ONE additional section-render after init settles (650 ms) so the form id reflects the
      // final restored state on sibling-arrival. This replaces the former 150/800/1500/3000 ms
      // cascade; a single call is enough because the section-render is authoritative. It is
      // cancelled on first user action via _initAborter.
      if (caratParam || sizeParam) {
        setTimeout(function () {
          if (initSignal.aborted || self._userHasActed) return;
          self._resolveAndCommitVariantViaSectionRender();
        }, 650);
      }
    } catch (e) { log('url-params error', e); }
  };

  // On a gold-side product (one with a Metal picker), handle metal selection honestly:
  //   - If we have a remembered gold metal AND arrived with ayer_carat/ayer_size, restore it.
  //   - If we have a remembered gold metal and the picker is empty, restore it.
  //   - If NO memory AND we arrived with ayer_carat/ayer_size, UNCHECK any metal Kalles may have
  //     auto-selected (Kalles' default is to pick the first-available metal; owner directive
  //     requires we not silently accept that when the shopper came from silver with no prior
  //     gold choice) and show a visible 'Please choose a metal' banner.
  //   - Otherwise leave Kalles' behavior alone (direct product visit without sibling handoff).
  Controller.prototype._restoreGoldMetalOrPrompt = function () {
    try {
      if (this._userHasActed) { log('restore: skip, user has acted'); return; }
      var metalRadios = this.section.querySelectorAll('input[type="radio"][name="Metal"]');
      if (!metalRadios.length) return;
      var params = new URLSearchParams(location.search);
      var explicitVariant = params.get('variant');
      if (explicitVariant) { log('restore: skip, explicit ?variant=' + explicitVariant); this._removeMetalPromptBanner(); return; }
      var key = this._familyKey();
      if (!key) return;
      var remembered = sessionStorage.getItem(key + ':gold_metal');
      var arrivedFromSibling = !!(params.get('ayer_carat') || params.get('ayer_size'));
      var checked = this.section.querySelector('input[type="radio"][name="Metal"]:checked');
      log('restore: entry remembered=' + remembered + ' arrivedFromSibling=' + arrivedFromSibling + ' checked=' + (checked ? checked.value : '(none)'));
      if (remembered) {
        // Restore remembered metal only when arrival-from-sibling (ayer_carat/size params)
        // OR when nothing is currently checked. Never restore on top of an existing explicit
        // customer selection that differs from memory.
        var target = this.section.querySelector('input[type="radio"][name="Metal"][value="' + CSS.escape(remembered) + '"]');
        if (!target) return;
        if (checked === target) { this._removeMetalPromptBanner(); return; }  // already correct
        if (!arrivedFromSibling && checked) {
          // Direct product visit: Kalles' choice stands; memory is only authoritative on
          // sibling-handoff flows (we rely on ayer_carat / ayer_size as the handoff signal).
          log('skip restore: no sibling handoff, Kalles already chose', checked.value);
          return;
        }
        if (checked) { checked.checked = false; }
        this._reassertInFlight = true;
        var selfInner = this;
        try {
          target.checked = true;
          target.dispatchEvent(new Event('change', { bubbles: true }));
          var curCarat = (this.section.querySelector('input[type="radio"][name="Center Diamond Weight"]:checked') || {}).value || '';
          var curSize = (this.section.querySelector('input[type="radio"][name="Ring Size (US)"]:checked') || {}).value || '';
          this._resolveAndCommitVariantViaSectionRender();
          log('restored remembered gold metal:', remembered);
        } finally {
          setTimeout(function () { selfInner._reassertInFlight = false; }, 0);
        }
        this._removeMetalPromptBanner();
        // Belt-and-suspenders: on the next animation frame, verify Kalles did not silently
        // revert our Metal choice (as it does when the exact combo falls outside its first-250
        // inline list). If it did, re-tick AND re-commit the variant id. One retry only to
        // avoid any possibility of ping-pong beyond the known Kalles behavior.
        requestAnimationFrame(function () {
          if (selfInner._userHasActed) return;
          var nowChecked = (selfInner.section.querySelector('input[type="radio"][name="Metal"]:checked') || {}).value || '';
          if (nowChecked === remembered) return;
          selfInner._reassertInFlight = true;
          try {
            var cur = selfInner.section.querySelector('input[type="radio"][name="Metal"]:checked');
            if (cur) cur.checked = false;
            target.checked = true;
            target.dispatchEvent(new Event('change', { bubbles: true }));
            var c2 = (selfInner.section.querySelector('input[type="radio"][name="Center Diamond Weight"]:checked') || {}).value || '';
            var s2 = (selfInner.section.querySelector('input[type="radio"][name="Ring Size (US)"]:checked') || {}).value || '';
            selfInner._resolveAndCommitVariantViaSectionRender();
            log('re-restored (Kalles reverted) remembered gold metal:', remembered);
          } finally {
            setTimeout(function () { selfInner._reassertInFlight = false; }, 0);
          }
        });
        return;
      }
      // No remembered metal + arrival-from-sibling: clear Kalles' silent auto-pick + prompt.
      // Kalles may auto-reselect a metal after our clear via its own observer / resolver, so
      // for the first ~1.5s we run a bounded rAF loop that keeps the Metal picker cleared.
      // The loop terminates on first user action, controller rollback, or when Kalles stops
      // re-selecting. This is NOT a timer cascade; it is a short bounded coordinator for one
      // specific race against Kalles' variant picker.
      if (arrivedFromSibling) {
        this._showMetalPromptBanner();
        // Backstop: disable purchase controls while the shopper has not yet explicitly picked
        // a Metal. Even if Kalles re-selects a default metal after our clear, the submit
        // + accelerated-checkout buttons stay disabled until the shopper's own change event
        // (isTrusted=true) triggers _resolveAndCommitVariantViaSectionRender -> re-enable.
        var liveFormX = this.section.querySelector('form[action*="/cart/add"]');
        if (liveFormX) this._setPurchaseControlsDisabled(liveFormX, true, 'please choose a metal');
        var selfCp = this;
        if (!this._clearPromptLoopActive) {
          this._clearPromptLoopActive = true;
          var frames = 0;
          var MAX = 180;  // ~3s @ 60fps - longer window because the Shopify dev server can lag
          var tick = function () {
            if (selfCp._userHasActed || selfCp._initAborter.signal.aborted || frames >= MAX) {
              selfCp._clearPromptLoopActive = false;
              return;
            }
            frames++;
            var cur = selfCp.section.querySelector('input[type="radio"][name="Metal"]:checked');
            if (cur && METAL_TO_COLOR_GROUP[cur.value]) {
              selfCp._reassertInFlight = true;
              try {
                cur.checked = false;
                if (frames === 1) cur.dispatchEvent(new Event('change', { bubbles: true }));
                if (frames === 1) log('cleared Kalles-auto-selected metal; prompting shopper explicitly:', cur.value);
              } finally {
                setTimeout(function () { selfCp._reassertInFlight = false; }, 0);
              }
            }
            // Keep the submit disabled every frame in case something (another app, Shopify)
            // tries to re-enable it.
            if (liveFormX) selfCp._setPurchaseControlsDisabled(liveFormX, true, 'please choose a metal');
            // Also re-show the banner each frame (idempotent) in case Kalles' section re-render
            // or any other DOM mutation removed it. The banner is the visible contract with the
            // shopper; it must survive the init-phase race.
            selfCp._showMetalPromptBanner();
            requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      }
    } catch (e) { log('_restoreGoldMetalOrPrompt error', e); }
  };

  Controller.prototype._removeMetalPromptBanner = function () {
    try {
      var el = this.section.querySelector('[data-ayer-metal-prompt]');
      if (el && el.parentNode) el.parentNode.removeChild(el);
    } catch (e) {}
  };

  Controller.prototype._showMetalPromptBanner = function () {
    try {
      // Insert once (idempotent)
      if (this.section.querySelector('[data-ayer-metal-prompt]')) return;
      var fieldset = this.section.querySelector('fieldset.hdt-product-form__input');
      if (!fieldset) return;
      var el = document.createElement('div');
      el.setAttribute('data-ayer-metal-prompt','true');
      el.setAttribute('role','status');
      el.style.cssText = 'background:#fff7d4;color:#875800;border:1px solid #c7b47a;border-radius:4px;padding:10px 12px;margin:0 0 10px;font:600 13px/1.3 inherit;';
      el.textContent = 'Please choose a metal to continue.';
      fieldset.parentNode.insertBefore(el, fieldset);
    } catch (e) {}
  };

  Controller.prototype._navigateSibling = function (btn) {
    var wrapper = btn.closest('[data-ayer-sibling-switch]');
    var siblingHandle = wrapper ? wrapper.getAttribute('data-ayer-sibling-handle') : null;
    if (!siblingHandle) return;
    // Before leaving: remember the current gold metal (if this is the gold side)
    this._rememberGoldMetal();
    // Read current carat + size from this product's form using scoped name+value selectors
    var carat = (this.section.querySelector('input[type="radio"][name="Center Diamond Weight"]:checked') || {}).value || '';
    var size = (this.section.querySelector('input[type="radio"][name="Ring Size (US)"]:checked') || {}).value || '';
    // Build URL. Preserve current preview_key / preview_theme_id if present
    var currentParams = new URLSearchParams(location.search);
    var dest = new URL('/products/' + siblingHandle, location.origin);
    var destParams = new URLSearchParams();
    if (currentParams.get('preview_key')) destParams.set('preview_key', currentParams.get('preview_key'));
    if (currentParams.get('preview_theme_id')) destParams.set('preview_theme_id', currentParams.get('preview_theme_id'));
    if (carat) destParams.set('ayer_carat', carat);
    if (size) destParams.set('ayer_size', size);
    dest.search = destParams.toString();
    log('sibling-navigate', dest.toString(), 'carat=' + carat, 'size=' + size);
    var suffix = wrapper.querySelector('[data-ayer-sibling-suffix]');
    if (suffix) suffix.textContent = (carat || size) ? ((carat || '') + (carat && size ? ' / ' : '') + (size || '')) : '';
    location.href = dest.toString();
  };

  Controller.prototype._eventTargetsThisSection = function (e) {
    if (!e) return false;
    if (e.sectionId && this.sectionId && e.sectionId === this.sectionId) return true;
    var el = e.target;
    while (el && el.nodeType === 1) {
      if (el === this.section) return true;
      el = el.parentNode;
    }
    return false;
  };

  Controller.prototype._rebind = function () {
    this.form = this.section.querySelector('form[action*="/cart/add"]');
    this.media = this.section.querySelector('hdt-product-media, .hdt-product-media, .product__media, [data-product-media]');
    var mount = this.section.querySelector('script[type="application/json"][data-ayer-color-group-hero-map]');
    if (mount) { this.mount = mount; this.heroMap = this._readMap(); }
  };

  Controller.prototype._currentColorGroup = function () {
    if (!this.section) return null;
    var checked = this.section.querySelectorAll('input[type="radio"]:checked');
    for (var i = 0; i < checked.length; i++) {
      var v = (checked[i].value || '').trim();
      if (METAL_TO_COLOR_GROUP[v]) return METAL_TO_COLOR_GROUP[v];
    }
    var sels = this.section.querySelectorAll('select');
    for (var j = 0; j < sels.length; j++) {
      var val = (sels[j].value || '').trim();
      if (METAL_TO_COLOR_GROUP[val]) return METAL_TO_COLOR_GROUP[val];
    }
    if (this.defaultColorGroup) return this.defaultColorGroup;
    return null;
  };

  Controller.prototype._mediaItems = function () {
    if (!this.media) return [];
    var items = this.media.querySelectorAll('hdt-slide, .hdt-slider__item, [data-media-id]');
    return Array.from(items);
  };

  Controller.prototype._thumbItems = function () {
    if (!this.section) return [];
    // Kalles renders thumbnails via snippets/media-thumb.liquid as .hdt-thumbnail__media (with
    // data-media-id). The fixture also exercises the legacy selectors below.
    var items = this.section.querySelectorAll('.hdt-thumbnail__media, [data-thumbnail-id], .hdt-product-thumbnail__item, .product__thumb');
    return Array.from(items);
  };

  // Decide visibility for a single item.
  // Policy table:
  //   group === null (no selection yet)                                    -> SHOWN
  //   itGroup === 'shared'                                                 -> SHOWN
  //   itGroup === group AND map[group] has items                           -> SHOWN
  //   itGroup === group AND map[group] is empty (no approved for group)    -> HIDDEN
  //       (metafield is the source of truth; obsolete tag does not keep item visible)
  //   AYER-managed + no itGroup                                            -> HIDDEN
  //   not-AYER-managed (no attr at all)                                    -> SHOWN
  //   itGroup && itGroup !== group                                         -> HIDDEN
  Controller.prototype._shouldShow = function (itGroup, isAyerManaged, group, hasImagesForGroup) {
    if (group === null) return true;
    if (itGroup === SHARED) return true;
    if (itGroup && itGroup === group) return hasImagesForGroup === true;
    if (!itGroup) return isAyerManaged ? false : true;
    return false;
  };

  Controller.prototype._applyVisibility = function (el, show) {
    if (show) {
      el.removeAttribute('data-ayer-hidden');
      el.removeAttribute('hidden');
      if (el.style.display === 'none') el.style.display = '';
    } else {
      el.setAttribute('data-ayer-hidden', 'true');
      el.setAttribute('hidden', '');
      el.style.display = 'none';
    }
  };

  // v7 lightbox ownership: a PhotoSwipe instance belongs to THIS controller if-and-only-if
  // we can establish an explicit linkage between PhotoSwipe's data source and OUR media items.
  // The ownership check is a conjunction: EITHER the instance object is stored on an element
  // we own (this.media or an opener descended from this.section), OR the instance's dataSource
  // array contains a slide whose .element is one of our media items. We do NOT rely on an image
  // src filename-prefix match in isolation because other galleries can legitimately reuse the
  // same Shopify CDN image.
  Controller.prototype._findOwnedPhotoSwipeInstance = function () {
    var candidate = null;
    // Preferred: a stored instance on OUR media / opener elements.
    if (this.media) {
      candidate = this.media._pswp || this.media._photoswipe || this.media.pswp || this.media.lightbox;
      if (!candidate && typeof this.media.getPswp === 'function') { try { candidate = this.media.getPswp(); } catch (e) {} }
      if (candidate) return candidate;
    }
    if (this.section) {
      var openers = this.section.querySelectorAll('hdt-open-lightbox-psw, hdt-open-lightbox, [data-open-lightbox]');
      for (var i = 0; i < openers.length; i++) {
        var o = openers[i];
        candidate = o._pswp || o._photoswipe || o.pswp || o.lightbox;
        if (candidate) return candidate;
      }
    }
    // Secondary: window.pswp whose dataSource is sourced from OUR media
    var inst = window.pswp || window.photoswipeInstance || window.__pswp;
    if (inst && inst.options && Array.isArray(inst.options.dataSource)) {
      var myItems = new Set(this._mediaItems());
      for (var j = 0; j < inst.options.dataSource.length; j++) {
        var d = inst.options.dataSource[j];
        if (d && d.element && myItems.has(d.element)) return inst;
      }
    }
    return null;
  };

  Controller.prototype._closeLightboxIfOpen = function () {
    var pswp = document.querySelector('.pswp.pswp--open');
    if (!pswp) return;
    var instance = this._findOwnedPhotoSwipeInstance();
    if (!instance) {
      log('a .pswp is open but is not owned by this controller; leaving alone');
      return;
    }
    // Use PhotoSwipe's own destroy() for synchronous teardown + its native scroll/focus restoration.
    try { if (typeof instance.destroy === 'function') instance.destroy(); else if (typeof instance.close === 'function') instance.close(); }
    catch (e) { log('instance teardown failed:', e); }
    // After destroy(), locate the .pswp element that PhotoSwipe just left behind (if any) and
    // remove ONLY that specific element. We do not touch other .pswp elements that may belong
    // to unrelated galleries, drawers, or overlays on the page.
    var ourPswp = (instance && instance.element) || pswp;
    try { if (ourPswp && ourPswp.isConnected && ourPswp.classList.contains('pswp')) ourPswp.remove(); } catch (e) {}
    // Clear window.pswp only when we are certain it referenced OUR instance.
    try { if (window.pswp === instance) window.pswp = null; } catch (e) {}
    // PhotoSwipe v5 may leave body / html overflow:hidden if destroy() runs mid-animation.
    // Restore scrollability explicitly only when it is still clamped and no OTHER .pswp
    // overlay is open (so we never un-clamp while a different gallery / drawer is active).
    try {
      if (!document.querySelector('.pswp.pswp--open')) {
        if (getComputedStyle(document.body).overflow === 'hidden') document.body.style.removeProperty('overflow');
        if (getComputedStyle(document.documentElement).overflow === 'hidden') document.documentElement.style.removeProperty('overflow');
      }
    } catch (e) {}
  };

  // Pre-filter hook: before PhotoSwipe opens on our gallery, remove hidden items from the
  // dataSource so navigation inside the lightbox cannot land on a stale group. This runs
  // automatically when Kalles fires its own 'lightbox:psw:open' event (we already listen).
  Controller.prototype._filterLightbox = function () {
    var self = this;
    // Run twice: once synchronously (catches dataSource configuration), once after a tick
    // (catches the newly-mounted .pswp__item elements before user interaction).
    var apply = function () {
      try {
        var hiddenIds = new Set();
        self._mediaItems().forEach(function (it) {
          if (it.hasAttribute('data-ayer-hidden')) {
            var mid = it.getAttribute('data-media-id'); if (mid) hiddenIds.add(String(mid));
          }
        });
        if (!hiddenIds.size) return;
        var instance = self._findOwnedPhotoSwipeInstance();
        if (instance && Array.isArray(instance.options.dataSource)) {
          // Build a filtered array keeping only slides that are NOT hidden
          var filtered = instance.options.dataSource.filter(function (d) {
            var mid = d && d.element && d.element.getAttribute('data-media-id');
            return !mid || !hiddenIds.has(String(mid));
          });
          instance.options.dataSource = filtered;
        }
        // Also mark the pre-mounted .pswp__item elements as hidden if their data-media-id is in
        // our hidden list; browsers will visually suppress and PhotoSwipe will skip navigation.
        var container = document.querySelector('.pswp__container');
        if (container) {
          container.querySelectorAll('.pswp__item').forEach(function (slide) {
            var mid = slide.getAttribute('data-media-id') || (slide.querySelector('[data-media-id]') ? slide.querySelector('[data-media-id]').getAttribute('data-media-id') : null);
            if (mid && hiddenIds.has(String(mid))) {
              slide.setAttribute('data-ayer-lightbox-hidden', 'true');
              slide.setAttribute('hidden', '');
              slide.style.display = 'none';
            }
          });
        }
      } catch (e) { log('_filterLightbox error', e); }
    };
    apply();
    setTimeout(apply, 50);
  };

  Controller.prototype.apply = function (force) {
    if (!this.heroMap) return;
    var group = this._currentColorGroup();
    // Dedupe only after we've successfully applied at least once.
    if (!force && this.lastApplyStamp !== 0 && group === this.lastColorGroup) return;
    this._closeLightboxIfOpen();
    this.lastColorGroup = group;
    this.lastApplyStamp = Date.now();
    var map = group ? this.heroMap[group] : null;
    var hasImagesForGroup = Array.isArray(map) && map.length > 0;
    var ayerManagedSection = this.section.hasAttribute('data-ayer-managed') || !!this.section.querySelector('[data-ayer-managed="true"], [data-ayer-color-group]');
    var self = this;
    // Main gallery
    this._mediaItems().forEach(function (it) {
      var itGroup = it.getAttribute('data-ayer-color-group');
      var itAyer = it.hasAttribute('data-ayer-managed') ? it.getAttribute('data-ayer-managed') === 'true' : ayerManagedSection;
      self._applyVisibility(it, self._shouldShow(itGroup, itAyer, group, hasImagesForGroup));
    });
    // Thumbnails (mirror policy exactly)
    this._thumbItems().forEach(function (it) {
      var itGroup = it.getAttribute('data-ayer-color-group');
      var itAyer = it.hasAttribute('data-ayer-managed') ? it.getAttribute('data-ayer-managed') === 'true' : ayerManagedSection;
      self._applyVisibility(it, self._shouldShow(itGroup, itAyer, group, hasImagesForGroup));
    });
    // Placeholder: only when a group IS selected AND the map has no images for it.
    this._removePlaceholder();
    if (group !== null && !hasImagesForGroup) this._renderPlaceholder(group);
    // Advance the slider to the first visible slide of this group.
    this._advanceSlider(group);
    log('section=' + this.sectionId + ' group=' + group + ' mainVisible=' + this._mediaItems().filter(function (i) { return !i.hasAttribute('data-ayer-hidden'); }).length + ' thumbVisible=' + this._thumbItems().filter(function (i) { return !i.hasAttribute('data-ayer-hidden'); }).length);
  };

  Controller.prototype._removePlaceholder = function () {
    if (!this.media) return;
    this.media.querySelectorAll('[data-ayer-placeholder]').forEach(function (n) { n.remove(); });
    this.placeholderEl = null;
  };

  Controller.prototype._renderPlaceholder = function (group) {
    if (!this.media) return;
    var ph = document.createElement('div');
    ph.setAttribute('data-ayer-placeholder', 'true');
    ph.setAttribute('data-media-id', 'ayer-placeholder-' + (group || 'unknown'));
    ph.className = 'hdt-slider__slide hdt-product__media-item';
    ph.style.cssText = 'display:flex;flex-direction:column;align-items:center;justify-content:center;background:#f1ece0;color:#6b5a2e;border:1px dashed #c7b47a;padding:48px 24px;font:14px/1.4 system-ui,sans-serif;min-height:420px;';
    ph.innerHTML = '<strong style="font-size:16px;margin-bottom:8px;">No approved image yet</strong>' +
                   '<span>Color group: ' + (group || 'unknown') + '</span>' +
                   '<span style="margin-top:6px;color:#8b7a4e;">Design concept pending manufacturing confirmation.</span>';
    var sliderContainer = this.media.querySelector('.hdt-slider__container') || this.media;
    sliderContainer.appendChild(ph);
    this.placeholderEl = ph;
  };

  Controller.prototype._advanceSlider = function (group) {
    if (!this.media) return;
    var items = this._mediaItems();
    var firstVisibleIdx = -1;
    for (var i = 0; i < items.length; i++) {
      if (!items[i].hasAttribute('data-ayer-hidden')) { firstVisibleIdx = i; break; }
    }
    if (firstVisibleIdx < 0 && this.placeholderEl) firstVisibleIdx = items.length;
    var slider = this.media.querySelector('hdt-slider');
    // Reset the slider's horizontal transform / scrollLeft BEFORE reInit so Kalles' slider
    // recomputes slide positions from zero. Without this, after we toggle display:none on
    // non-matching slides, the slider's cached horizontal position can leave the first
    // visible slide translated off-screen (observed with Pixel-8 viewport + yellow_gold
    // filter: slide ends up at x = -348, blocking a real customer tap).
    if (slider) {
      try {
        var container = slider.querySelector('.hdt-slider__container');
        var viewport = slider.querySelector('.hdt-slider__viewport');
        if (container) { container.style.transform = 'translate3d(0px, 0px, 0px)'; container.style.left = ''; }
        if (viewport) { viewport.scrollLeft = 0; }
      } catch (e) { log('slider position reset failed', e); }
    }
    if (slider && typeof slider.reInit === 'function') { try { slider.reInit(); } catch (e) { log('slider.reInit failed', e); } }
    // After hidden-filter, Kalles' goToIndex(N) still computes positions based on the ORIGINAL
    // DOM order including display:none slides. On the Pixel-8 mobile viewport that caused the
    // first visible slide to end up translated off-screen (observed at x = -348). Pass index 0
    // so the slider aligns the first VISIBLE slide (which is now at layout position 0) with the
    // viewport. If Kalles ever changes to count only visible slides, this is still correct.
    if (slider && typeof slider.goToIndex === 'function' && firstVisibleIdx >= 0) {
      try { slider.goToIndex(0); } catch (e) { log('slider.goToIndex failed', e); }
    }
    // Second-pass position reset AFTER reInit + goToIndex: some Kalles builds compute
    // slide offsets lazily and the first paint still shows the stale transform. Run on
    // next animation frame and again at microtask time.
    if (slider) {
      requestAnimationFrame(function () {
        try {
          var container = slider.querySelector('.hdt-slider__container');
          var firstVisible = slider.querySelector('.hdt-slider__slide[data-media-id]:not([data-ayer-hidden])');
          if (!firstVisible || !container) return;
          var slideRect = firstVisible.getBoundingClientRect();
          var viewportRect = (slider.querySelector('.hdt-slider__viewport') || slider).getBoundingClientRect();
          var offset = slideRect.left - viewportRect.left;
          if (Math.abs(offset) > 1) {
            // Pull the container LEFT by the offset so the first visible slide aligns with viewport 0.
            var current = (container.style.transform || '').match(/translate3d\((-?[\d.]+)px/);
            var currentX = current ? parseFloat(current[1]) : 0;
            container.style.transform = 'translate3d(' + (currentX - offset) + 'px, 0px, 0px)';
          }
        } catch (e) {}
      });
    }
    var thumbSlider = this.section.querySelector('hdt-slider-thumb');
    if (thumbSlider && typeof thumbSlider.reInit === 'function') { try { thumbSlider.reInit(); } catch (e) {} }
  };

  Controller.prototype.rollback = function () {
    try { this.aborter.abort(); } catch (e) {}
    try { this._initAborter.abort('controller-rollback'); } catch (e) {}
    this._mediaItems().forEach(function (it) {
      it.removeAttribute('data-ayer-hidden'); it.removeAttribute('hidden'); it.style.display = '';
    });
    this._thumbItems().forEach(function (it) {
      it.removeAttribute('data-ayer-hidden'); it.removeAttribute('hidden'); it.style.display = '';
    });
    document.querySelectorAll('[data-ayer-lightbox-hidden]').forEach(function (n) {
      n.removeAttribute('data-ayer-lightbox-hidden'); n.removeAttribute('hidden'); n.style.display = '';
    });
    this._removePlaceholder();
    this.lastColorGroup = null;
    this.lastApplyStamp = 0;
  };

  // ---- Discovery + boot --------------------------------------------------
  function findAncestorSection(el) {
    var cur = el;
    while (cur && cur.nodeType === 1) {
      if (cur.tagName === 'HDT-MAIN-PRODUCT') return cur;
      if (cur.hasAttribute && cur.hasAttribute('data-section-type') && /product/i.test(cur.getAttribute('data-section-type'))) return cur;
      if (cur.hasAttribute && cur.hasAttribute('data-section-id') && cur.querySelector('form[action*="/cart/add"]')) return cur;
      cur = cur.parentNode;
    }
    var form = el.ownerDocument.querySelector('form[action*="/cart/add"]');
    return form ? (form.closest('[data-section-id]') || form.parentNode) : null;
  }

  function discover() {
    var mounts = document.querySelectorAll('script[type="application/json"][data-ayer-color-group-hero-map]');
    mounts.forEach(function (m) {
      if (controllers.has(m)) return;
      var c = new Controller(m);
      if (c && c.heroMap) controllers.set(m, c);
    });
  }

  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  onReady(function () {
    discover();
    setTimeout(discover, 2000);
  });

  window.AYER_METAL_GALLERY = {
    discover: discover,
    controllers: controllers,
    rollback: function () { controllers.forEach(function (c) { c.rollback(); }); },
    metalMap: METAL_TO_COLOR_GROUP,
    version: 'v18',
    allowedSizes: AYER_ALLOWED_SIZES_US,
    sizeOptionName: AYER_SIZE_OPTION_NAME
  };
})();
